import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Loader2, ShieldAlert, RefreshCw, Building2, Landmark, UserRound,
  Info, ArrowRight, PartyPopper, Lock,
} from "lucide-react";

import { Layout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getOnboardingAccess, submitOnboarding } from "@/lib/api";

const SUCCESS_BG =
  "https://images.unsplash.com/photo-1645290099737-23f17b0b7dd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwYWJzdHJhY3QlMjBibGFjayUyMGdvbGR8ZW58MHx8fHwxNzg3NTUyODE0fDA&ixlib=rb-4.1.0&q=85";

const schema = z.object({
  legal_entity_type: z.string().min(1, "Select an entity type"),
  nature_of_business: z.string().min(2, "Nature of business is required"),
  date_of_incorporation: z.string().min(1, "Date of incorporation is required"),
  company_website: z.string().optional().or(z.literal("")),
  annual_turnover: z.string().min(1, "Select an annual turnover range"),
  bank_account_name: z.string().min(2, "Account holder name is required"),
  bank_account_number: z.string().regex(/^\d{6,18}$/, "Enter a valid account number"),
  bank_name: z.string().min(2, "Bank name is required"),
  ifsc_code: z.string().regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Enter a valid IFSC (e.g. HDFC0001234)"),
  account_type: z.string().min(1, "Select an account type"),
  authorized_signatory_name: z.string().min(2, "Signatory name is required"),
  designation: z.string().min(2, "Designation is required"),
  contact_email: z.string().email("Enter a valid email"),
  contact_phone: z.string().min(7, "Contact number is required"),
});

const inputClass =
  "bg-white/5 border-white/10 text-white placeholder:text-zinc-600 rounded-xl h-11 caret-[#F5C518] transition-colors duration-200 hover:border-white/25 focus-visible:ring-1 focus-visible:ring-[#F5C518] focus-visible:border-[#F5C518]";
const selectClass =
  "bg-white/5 border-white/10 text-white rounded-xl h-11 transition-colors duration-200 hover:border-white/25 focus:ring-1 focus:ring-[#F5C518] focus:border-[#F5C518] data-[placeholder]:text-zinc-600";

const Field = ({ id, label, optional, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm text-zinc-300 leading-relaxed">
      {label}
      {optional && <span className="text-zinc-600 font-normal ml-1">(optional)</span>}
    </Label>
    {children}
    {error && (
      <p data-testid={`error-${id}`} className="text-xs text-red-400">{error.message}</p>
    )}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 text-[#F5C518]" />
      <h2 className="font-heading text-lg font-semibold tracking-tight">{title}</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">{children}</div>
  </div>
);

export default function Onboarding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [state, setState] = React.useState("loading"); // loading | error | form | complete
  const [errorMsg, setErrorMsg] = React.useState("");

  const {
    register, handleSubmit, setValue, watch, reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  React.useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState("error");
      setErrorMsg("This link is missing its session token. Please use the link from your onboarding flow.");
      return;
    }
    (async () => {
      try {
        const data = await getOnboardingAccess(token);
        if (cancelled) return;
        if (data.status === "onboarding_complete") {
          setState("complete");
          return;
        }
        const bd = data.business_details || {};
        const od = data.onboarding_details || {};
        reset({
          legal_entity_type: od.legal_entity_type || "",
          nature_of_business: od.nature_of_business || "",
          date_of_incorporation: od.date_of_incorporation || "",
          company_website: od.company_website || "",
          annual_turnover: od.annual_turnover || "",
          bank_account_name: od.bank_account_name || bd.full_name || "",
          bank_account_number: od.bank_account_number || "",
          bank_name: od.bank_name || "",
          ifsc_code: od.ifsc_code || "",
          account_type: od.account_type || "",
          authorized_signatory_name: od.authorized_signatory_name || bd.full_name || "",
          designation: od.designation || "",
          contact_email: od.contact_email || bd.email || "",
          contact_phone: od.contact_phone || bd.phone_whatsapp || "",
        });
        setState("form");
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setErrorMsg(
          e?.response?.data?.detail ||
            "We couldn't load your onboarding form. Please try again shortly."
        );
      }
    })();
    return () => { cancelled = true; };
  }, [token, reset]);

  const onSubmit = async (values) => {
    try {
      await submitOnboarding(token, values);
      setState("complete");
    } catch (e) {
      setErrorMsg(
        e?.response?.data?.detail || "We couldn't save your details. Please try again."
      );
      setState("error");
    }
  };

  return (
    <Layout currentStep={3}>
      {state !== "complete" && (
        <PageHeader
          step={3}
          title="Onboarding"
          subtitle="Just a few final details to get your account ready. We've pre-filled what we already have from your application."
        />
      )}

      {state === "loading" && (
        <div data-testid="onboarding-loading" className="bg-[#121212] rounded-2xl border border-white/10 p-16 shadow-2xl shadow-black/50 flex flex-col items-center justify-center animate-fade-slide-up">
          <Loader2 className="h-8 w-8 text-[#F5C518] animate-spin" />
          <p className="mt-4 font-body text-sm text-zinc-400">Verifying your secure link…</p>
        </div>
      )}

      {state === "error" && (
        <div data-testid="onboarding-error" className="bg-[#121212] rounded-2xl border border-red-500/25 p-10 sm:p-14 shadow-2xl shadow-black/50 text-center animate-fade-slide-up">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 border border-red-500/30">
            <ShieldAlert className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="mt-6 font-heading text-2xl font-semibold tracking-tight">We can't open your onboarding</h2>
          <p data-testid="onboarding-error-message" className="mt-3 font-body text-sm text-zinc-400 max-w-md mx-auto leading-relaxed">{errorMsg}</p>
          <button data-testid="onboarding-restart-btn" onClick={() => navigate("/")} className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-transparent text-white px-6 h-11 hover:bg-white/5 hover:border-white/25 active:bg-white/10 transition-colors duration-200 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C518]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]">
            <RefreshCw className="h-4 w-4" /> Start over
          </button>
        </div>
      )}

      {state === "form" && (
        <form data-testid="onboarding-form" onSubmit={handleSubmit(onSubmit)} className="animate-fade-slide-up">
          <div data-testid="onboarding-load-note" className="mb-6 flex items-start gap-2 rounded-xl border border-[#F5C518]/20 bg-[#F5C518]/5 px-4 py-3 text-sm text-[#F5C518]/90">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>It may take a few moments to load, please complete all fields and submit.</span>
          </div>

          <div className="bg-[#121212] rounded-2xl border border-white/10 p-6 sm:p-10 shadow-2xl shadow-black/50 space-y-10">
            <Section icon={Building2} title="Company Information">
              <Field id="legal_entity_type" label="Legal Entity Type" error={errors.legal_entity_type}>
                <Select value={watch("legal_entity_type")} onValueChange={(v) => setValue("legal_entity_type", v, { shouldValidate: true })}>
                  <SelectTrigger data-testid="input-legal_entity_type" className={selectClass}><SelectValue placeholder="Select entity type" /></SelectTrigger>
                  <SelectContent>
                    {["Proprietorship", "Partnership", "Private Limited", "LLP", "Public Limited", "Other"].map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field id="nature_of_business" label="Nature of Business" error={errors.nature_of_business}>
                <Input id="nature_of_business" data-testid="input-nature_of_business" placeholder="e.g. Travel & Adventure" className={inputClass} {...register("nature_of_business")} />
              </Field>
              <Field id="date_of_incorporation" label="Date of Incorporation" error={errors.date_of_incorporation}>
                <Input id="date_of_incorporation" type="date" data-testid="input-date_of_incorporation" className={`${inputClass} [color-scheme:dark]`} {...register("date_of_incorporation")} />
              </Field>
              <Field id="company_website" label="Company Website" optional error={errors.company_website}>
                <Input id="company_website" data-testid="input-company_website" placeholder="https://…" className={inputClass} {...register("company_website")} />
              </Field>
              <Field id="annual_turnover" label="Annual Turnover" error={errors.annual_turnover}>
                <Select value={watch("annual_turnover")} onValueChange={(v) => setValue("annual_turnover", v, { shouldValidate: true })}>
                  <SelectTrigger data-testid="input-annual_turnover" className={selectClass}><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    {["< ₹20 Lakh", "₹20 Lakh – ₹1 Cr", "₹1 Cr – ₹5 Cr", "₹5 Cr – ₹25 Cr", "> ₹25 Cr"].map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </Section>

            <div className="h-px bg-white/10" />

            <Section icon={Landmark} title="Banking Details">
              <Field id="bank_account_name" label="Account Holder Name" error={errors.bank_account_name}>
                <Input id="bank_account_name" data-testid="input-bank_account_name" className={inputClass} {...register("bank_account_name")} />
              </Field>
              <Field id="bank_account_number" label="Account Number" error={errors.bank_account_number}>
                <Input id="bank_account_number" inputMode="numeric" data-testid="input-bank_account_number" className={inputClass} {...register("bank_account_number")} />
              </Field>
              <Field id="bank_name" label="Bank Name" error={errors.bank_name}>
                <Input id="bank_name" data-testid="input-bank_name" placeholder="e.g. HDFC Bank" className={inputClass} {...register("bank_name")} />
              </Field>
              <Field id="ifsc_code" label="IFSC Code" error={errors.ifsc_code}>
                <Input id="ifsc_code" data-testid="input-ifsc_code" placeholder="HDFC0001234" className={`${inputClass} uppercase`} {...register("ifsc_code")} onChange={(e) => setValue("ifsc_code", e.target.value.toUpperCase())} />
              </Field>
              <Field id="account_type" label="Account Type" error={errors.account_type}>
                <Select value={watch("account_type")} onValueChange={(v) => setValue("account_type", v, { shouldValidate: true })}>
                  <SelectTrigger data-testid="input-account_type" className={selectClass}><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {["Savings", "Current"].map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>
            </Section>

            <div className="h-px bg-white/10" />

            <Section icon={UserRound} title="Point of Contact">
              <Field id="authorized_signatory_name" label="Authorised Signatory Name" error={errors.authorized_signatory_name}>
                <Input id="authorized_signatory_name" data-testid="input-authorized_signatory_name" className={inputClass} {...register("authorized_signatory_name")} />
              </Field>
              <Field id="designation" label="Designation" error={errors.designation}>
                <Input id="designation" data-testid="input-designation" placeholder="e.g. Director" className={inputClass} {...register("designation")} />
              </Field>
              <Field id="contact_email" label="Contact Email" error={errors.contact_email}>
                <Input id="contact_email" type="email" data-testid="input-contact_email" className={inputClass} {...register("contact_email")} />
              </Field>
              <Field id="contact_phone" label="Contact Phone" error={errors.contact_phone}>
                <Input id="contact_phone" data-testid="input-contact_phone" className={inputClass} {...register("contact_phone")} />
              </Field>
            </Section>
          </div>

          <button type="submit" data-testid="onboarding-submit-btn" disabled={isSubmitting} className="mt-8 w-full h-12 rounded-full bg-[#F5C518] text-black font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:bg-[#ffd633] hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(245,197,24,0.3)] active:scale-[0.98] active:bg-[#ffd633] touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C518]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed">
            {isSubmitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>) : (<>Complete Onboarding <ArrowRight className="h-4 w-4" /></>)}
          </button>
        </form>
      )}

      {state === "complete" && (
        <div data-testid="onboarding-complete" className="relative overflow-hidden bg-[#121212] rounded-2xl border border-white/10 shadow-2xl shadow-black/50 animate-fade-slide-up">
          <img src={SUCCESS_BG} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
          <div className="relative px-4 sm:px-8 py-16 sm:py-20 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F5C518] shadow-[0_0_30px_rgba(245,197,24,0.5)]">
              <PartyPopper className="h-8 w-8 text-black" />
            </div>
            <h1 className="mt-8 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Almost there! You're all set to{" "}
              <span className="whitespace-nowrap">get started 🎉</span>
            </h1>

            <div
              data-testid="onboarding-form-embed"
              className="mt-10 -mx-4 sm:mx-0 text-left bg-[#0f0f0f] rounded-none sm:rounded-2xl border-y sm:border border-white/10 overflow-hidden shadow-xl shadow-black/40"
            >
              <iframe
                data-testid="google-form-iframe"
                title="REC Ventures Onboarding Form"
                src="https://docs.google.com/forms/d/e/1FAIpQLScwhXE82CIc23nS7k69DX1hC39Ssy4hISEMXiHPVmOxvk9xTw/viewform?embedded=true"
                className="w-full h-[88vh] min-h-[720px] sm:h-[72vh] sm:min-h-[560px] bg-white block"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
              >
                Loading…
              </iframe>
            </div>

            <div className="mt-10 inline-flex items-center gap-2 text-xs text-zinc-500 border-t border-white/10 pt-6">
              <Lock className="h-3.5 w-3.5 text-[#F5C518]" />
              Your information is secure and encrypted.
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
